import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
} from '@mui/material';
import {
  MenuBook as ManualsIcon,
  Inventory as InventoryIcon,
  Build as ToolsIcon,
  LocalGasStation as SuppliesIcon,
} from '@mui/icons-material';
import { Machine } from '../../types/machine';
import { format, isValid, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { MachineActivityList } from '../activities/MachineActivityList';

interface MachineOverviewTabProps {
  machine: Machine;
}

// Helper function to safely format dates
const formatDate = (dateString: string | undefined | null, formatStr: string = 'dd. MMMM yyyy, HH:mm'): string => {
  if (!dateString || dateString === 'null' || dateString === 'undefined') {
    return 'Nicht verfügbar';
  }
  
  try {
    let date: Date;
    
    if (typeof dateString === 'string') {
      if (dateString.trim() === '' || dateString === '0000-00-00T00:00:00Z') {
        return 'Nicht verfügbar';
      }
      date = parseISO(dateString);
    } else {
      date = new Date(dateString as any);
    }
    
    if (!isValid(date) || date.getFullYear() < 1900) {
      return 'Ungültiges Datum';
    }
    
    return format(date, formatStr, { locale: de });
  } catch (error) {
    console.warn('Error formatting date in MachineOverviewTab:', dateString, error);
    return 'Ungültiges Datum';
  }
};

export const MachineOverviewTab: React.FC<MachineOverviewTabProps> = ({ machine }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleCardClick = (cardTitle: string) => {
    const tabMapping: { [key: string]: string } = {
      'Anleitungen': 'manuals',
      'Ersatzteile': 'spare-parts',
      'Betriebsmittel': 'supplies',
      'Werkzeuge': 'tools',
    };

    const tabParam = tabMapping[cardTitle];
    if (tabParam) {
      navigate(`${location.pathname}?tab=${tabParam}`);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational':
        return 'success';
      case 'maintenance':
        return 'warning';
      case 'out_of_order':
        return 'error';
      case 'decommissioned':
        return 'default';
      default:
        return 'default';
    }
  };

  const statusLabels = {
    operational: 'Betriebsbereit',
    maintenance: 'Wartung',
    out_of_order: 'Außer Betrieb',
    decommissioned: 'Stillgelegt',
  };

  const summaryCards = [
    {
      title: 'Anleitungen',
      count: machine.manuals?.length || 0,
      icon: <ManualsIcon />,
      color: '#1976d2',
    },
    {
      title: 'Ersatzteile',
      count: machine.spareParts?.length || 0,
      icon: <InventoryIcon />,
      color: '#f57c00',
    },
    {
      title: 'Betriebsmittel',
      count: machine.operatingSupplies?.length || 0,
      icon: <SuppliesIcon />,
      color: '#388e3c',
    },
    {
      title: 'Werkzeuge',
      count: machine.tools?.length || 0,
      icon: <ToolsIcon />,
      color: '#7b1fa2',
    },
  ];

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Summary Cards */}
        <Grid size={{ xs: 12 }}>
          <Typography variant="h6" gutterBottom>
            Übersicht
          </Typography>
          <Grid container spacing={2}>
            {summaryCards.map((card) => (
              <Grid size={{ xs: 6, sm: 3 }} key={card.title}>
                <Card
                  onClick={() => handleCardClick(card.title)}
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    border: `2px solid ${card.color}20`,
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: (theme) => theme.shadows[4],
                      bgcolor: (theme) => theme.palette.action.hover,
                    },
                    '&:active': {
                      transform: 'translateY(0px)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`${card.title} öffnen - ${card.count} Elemente`}
                >
                  <Box
                    sx={{
                      color: card.color,
                      mb: 1,
                      '& svg': {
                        fontSize: 40,
                      },
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Typography variant="h4" component="div" sx={{ color: card.color, mb: 1 }}>
                    {card.count}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {card.title}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Machine Details */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Maschineninformationen
              </Typography>
              <List>
                <ListItem>
                  <ListItemText
                    primary="Name"
                    secondary={machine.name}
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemText
                    primary="Status"
                    secondary={
                      <Chip
                        label={statusLabels[machine.status as keyof typeof statusLabels]}
                        color={getStatusColor(machine.status) as any}
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    }
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemText
                    primary="Standort"
                    secondary={machine.location}
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemText
                    primary="Beschreibung"
                    secondary={machine.description || 'Keine Beschreibung verfügbar'}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Timeline */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Zeitlinie
              </Typography>
              <List>
                <ListItem>
                  <ListItemText
                    primary="Erstellt"
                    secondary={machine?.createdAt ? formatDate(machine.createdAt) : 'Lädt...'}
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemText
                    primary="Letzte Aktualisierung"
                    secondary={machine?.updatedAt ? formatDate(machine.updatedAt) : 'Lädt...'}
                  />
                </ListItem>
                {machine.imageUrl && (
                  <>
                    <Divider />
                    <ListItem>
                      <ListItemText
                        primary="Bild vorhanden"
                        secondary="Ja"
                      />
                    </ListItem>
                  </>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Letzte Aktivitäten
              </Typography>
              <MachineActivityList machineId={machine.id} limit={10} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};